'use client'

import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import * as React from 'react'
import { ReactNode, SVGProps } from 'react'

import { cn } from '@/utils'

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
	<AccordionPrimitive.Item ref={ref} className={cn(className)} {...props} />
))
AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
		iconColor?: string
		icon?: (props: SVGProps<SVGSVGElement>) => ReactNode
		iconPosition?: 'left' | 'right'
	}
>(
	(
		{ className, children, iconColor, icon, iconPosition = 'right', ...props },
		ref,
	) => {
		const Icon = icon ?? LineChevronDownSmall
		return (
			<AccordionPrimitive.Header className="flex">
				<AccordionPrimitive.Trigger
					ref={ref}
					className={cn(
						'text-sm type-body-medium font-semibold flex flex-1 items-center justify-between py-4 text-left transition-all [&[data-state=open]>svg]:rotate-180',
						className,
					)}
					{...props}
				>
					{iconPosition === 'left' && (
						<Icon
							className={cn(
								'shrink-0 transition-transform duration-200',
								iconColor,
							)}
						/>
					)}

					{children}

					{iconPosition === 'right' && (
						<Icon
							className={cn(
								'shrink-0 transition-transform duration-200 h-6 w-6',
								iconColor,
							)}
						/>
					)}
				</AccordionPrimitive.Trigger>
			</AccordionPrimitive.Header>
		)
	},
)
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<AccordionPrimitive.Content
		ref={ref}
		className="text-sm overflow-hidden will-change-transform data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
		{...props}
	>
		<div className={cn('pb-4 pt-0', className)}>{children}</div>
	</AccordionPrimitive.Content>
))
AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger }
